import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    FileText,
    LayoutGrid,
    List,
    Loader2,
    Scale,
    Stethoscope,
    Timer,
    ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import TahkimKanban from "../../components/tahkim/TahkimKanban";
import TahkimMerkezHero from "../../components/tahkim/TahkimMerkezHero";
import TahkimOnTahkimGelismis from "./tahkim-merkezi/TahkimOnTahkimGelismis";
import TahkimBasvuruAsamasiPanel from "./tahkim-merkezi/TahkimBasvuruAsamasiPanel";
import TahkimRaporPdfPanel from "./tahkim-merkezi/TahkimRaporPdfPanel";
import { tahkimMerkez, TAHKIM_ASAMALARI, ODEME_DURUMLARI } from "../../lib/tahkimApi";

const TABS = [
    { key: "on_tahkim", label: "Ön Tahkim Komuta", icon: Timer },
    { key: "basvuru_asamasi", label: "Başvuru & Sürükle-Bırak", icon: ClipboardList },
    { key: "ozet", label: "Komuta", icon: Scale },
    { key: "kanban", label: "Kanban", icon: LayoutGrid },
    { key: "liste", label: "Operasyon listesi", icon: List },
    { key: "rapor", label: "PDF Raporlar", icon: FileText },
];

const ASAMA_COLORS = {
    ON_TAHKIM: "bg-sky-500",
    BASVURU: "bg-cyan-500",
    HAKEM: "bg-violet-500",
    BILIRKISI: "bg-amber-500",
    ARA_KARAR: "bg-emerald-500",
    KARAR: "bg-[#D4AF37]",
    ODEME: "bg-orange-500",
    KAPANDI: "bg-slate-500",
};

function KpiCard({ label, value, hint, accent, onClick, active }) {
    const rings = {
        red: "border-rose-500/40 bg-gradient-to-br from-rose-500/12 to-[#0B0F19]/90",
        amber: "border-amber-500/40 bg-gradient-to-br from-amber-500/12 to-[#0B0F19]/90",
        cyan: "border-cyan-500/35 bg-gradient-to-br from-cyan-500/10 to-[#0B0F19]/90",
        violet: "border-violet-500/35 bg-gradient-to-br from-violet-500/10 to-[#0B0F19]/90",
    };
    const Tag = onClick ? "button" : "div";
    return (
        <Tag
            type={onClick ? "button" : undefined}
            onClick={onClick}
            className={`rounded-xl border p-4 text-left transition-all ${rings[accent] || rings.violet} ${
                onClick ? "hover:scale-[1.02] cursor-pointer hover:border-violet-400/50" : ""
            } ${active ? "ring-2 ring-violet-400/50" : ""}`}
        >
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
            <div className="font-display text-2xl md:text-3xl text-white mt-1 tabular-nums">
                {value ?? "—"}
            </div>
            {hint ? <div className="text-[11px] text-slate-500 mt-1.5">{hint}</div> : null}
        </Tag>
    );
}

export default function TahkimMerkezPage() {
    const [tab, setTab] = useState("on_tahkim");
    const [ozet, setOzet] = useState(null);
    const [kanban, setKanban] = useState(null);
    const [liste, setListe] = useState([]);
    const [sirketRapor, setSirketRapor] = useState([]);
    const [saglik, setSaglik] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({});
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(new Set());
    const [topluAsama, setTopluAsama] = useState("");
    const [topluNot, setTopluNot] = useState("");

    const params = useMemo(() => {
        const p = { ...filters };
        if (q.trim()) p.q = q.trim();
        return p;
    }, [filters, q]);

    const loadOzet = useCallback(async () => {
        const [o, s] = await Promise.all([tahkimMerkez.ozet(), tahkimMerkez.saglik()]);
        setOzet(o);
        setSaglik(s);
    }, []);

    const loadKanban = useCallback(async () => {
        setKanban(await tahkimMerkez.kanban(params));
    }, [params]);

    const loadListe = useCallback(async () => {
        const data = await tahkimMerkez.list({ ...params, page_size: 100 });
        setListe(Array.isArray(data) ? data : data.results || []);
    }, [params]);

    const loadRapor = useCallback(async () => {
        const data = await tahkimMerkez.raporSirket();
        setSirketRapor(data.sirketler || []);
    }, []);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            await loadOzet();
            if (tab === "kanban") await loadKanban();
            if (tab === "liste") await loadListe();
            if (tab === "rapor") await loadRapor();
        } catch {
            toast.error("Veri yüklenemedi");
        } finally {
            setLoading(false);
        }
    }, [tab, loadOzet, loadKanban, loadListe, loadRapor]);

    useEffect(() => {
        refresh();
    }, [tab, params]);

    const toggleSelect = (id) => {
        setSelected((prev) => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id);
            else n.add(id);
            return n;
        });
    };

    const topluUygula = async () => {
        if (!selected.size) {
            toast.error("Kayıt seçin");
            return;
        }
        try {
            const r = await tahkimMerkez.topluGuncelle({
                ids: [...selected],
                tahkim_asamasi: topluAsama || undefined,
                panel_notlar_ek: topluNot || undefined,
                sync_dosya_durumu: true,
            });
            toast.success(`${r.guncellenen} kayıt güncellendi`);
            setSelected(new Set());
            refresh();
        } catch {
            toast.error("Toplu güncelleme başarısız");
        }
    };

    const runBackfill = async () => {
        try {
            const r = await tahkimMerkez.backfill();
            toast.success(`Backfill: +${r.olusturulan} yeni, ${r.guncellenen} güncellendi`);
            refresh();
        } catch {
            toast.error("Backfill başarısız");
        }
    };

    const exportCsv = async () => {
        try {
            await tahkimMerkez.exportCsv(params);
        } catch {
            toast.error("Dışa aktarma başarısız");
        }
    };

    const setHatirlatma = (key) => {
        setFilters((f) => {
            const n = { ...f };
            delete n.hatirlatma;
            if (key) n.hatirlatma = key;
            return n;
        });
        setTab("liste");
    };

    const asamaMax = useMemo(() => {
        if (!ozet?.asama) return 1;
        return Math.max(1, ...Object.values(ozet.asama));
    }, [ozet]);

    return (
        <div className="p-4 lg:p-8 max-w-[1600px] mx-auto animate-fade-in">
            <TahkimMerkezHero
                loading={loading}
                onRefresh={refresh}
                onExport={exportCsv}
                onRaporTab={() => setTab("rapor")}
                toplam={ozet?.toplam}
                onTahkimTab={setTab}
            />

            <div className="flex flex-wrap gap-1.5 p-1 rounded-xl border border-white/10 bg-[#0B0F19]/80 mb-4">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => setTab(t.key)}
                        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition flex-1 sm:flex-none justify-center ${
                            tab === t.key
                                ? "bg-violet-600/40 text-white shadow-inner border border-violet-500/40"
                                : "text-slate-400 hover:text-white hover:bg-white/5"
                        }`}
                    >
                        <t.icon className="h-4 w-4" />
                        <span className="hidden sm:inline">{t.label}</span>
                        <span className="sm:hidden">{t.label.split(" ")[0]}</span>
                    </button>
                ))}
            </div>

            <div className="flex flex-wrap gap-2 mb-5 items-end rounded-xl border border-white/10 bg-[#121A2F]/50 p-3">
                <input
                    type="search"
                    placeholder="Dosya no, plaka, hakem, başvuru no…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm text-white min-w-[220px] flex-1"
                />
                <select
                    value={filters.tahkim_asamasi || ""}
                    onChange={(e) =>
                        setFilters((f) => ({
                            ...f,
                            tahkim_asamasi: e.target.value || undefined,
                        }))
                    }
                    className="rounded-lg bg-[#0B0F19] border border-white/10 px-2 py-2 text-sm text-white"
                >
                    <option value="">Tüm aşamalar</option>
                    {TAHKIM_ASAMALARI.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
                <select
                    value={filters.odeme_durumu || ""}
                    onChange={(e) =>
                        setFilters((f) => ({
                            ...f,
                            odeme_durumu: e.target.value || undefined,
                        }))
                    }
                    className="rounded-lg bg-[#0B0F19] border border-white/10 px-2 py-2 text-sm text-white"
                >
                    <option value="">Ödeme durumu</option>
                    {ODEME_DURUMLARI.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
            </div>

            {saglik?.backfill_onerilir && (
                <div className="mb-5 flex flex-wrap items-center gap-2 rounded-xl border border-amber-500/35 bg-gradient-to-r from-amber-500/12 to-transparent px-4 py-3 text-sm text-amber-100">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <span>
                        <strong>{saglik.eksik_tahkim_kaydi}</strong> sigorta dosyasında tahkim
                        kaydı eksik — senkron önerilir.
                    </span>
                    <button
                        type="button"
                        onClick={runBackfill}
                        className="ml-auto px-3 py-1.5 rounded-lg bg-amber-600/50 hover:bg-amber-600/70 text-xs font-semibold"
                    >
                        Senkronize et
                    </button>
                </div>
            )}

            {tab === "ozet" && ozet && (
                <div className="space-y-6">
                    <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
                        <KpiCard
                            label="Toplam tahkim"
                            value={ozet.toplam}
                            accent="violet"
                            hint="Aktif STK dosyaları"
                        />
                        <KpiCard
                            label="Geciken ödeme"
                            value={ozet.odeme_geciken}
                            accent="red"
                            hint="Son ödeme tarihi geçmiş"
                            onClick={() => setHatirlatma("geciken_odeme")}
                            active={filters.hatirlatma === "geciken_odeme"}
                        />
                        <KpiCard
                            label="7 gün içinde ödeme"
                            value={ozet.odeme_yakin_7}
                            accent="amber"
                            hint="Yaklaşan son ödeme"
                            onClick={() => setHatirlatma("yakin_odeme")}
                            active={filters.hatirlatma === "yakin_odeme"}
                        />
                        <KpiCard
                            label="Hakem bekleyen"
                            value={ozet.hakem_bekleyen}
                            accent="cyan"
                            hint="Başvuru / hakem aşaması"
                            onClick={() => setHatirlatma("hakem_bekliyor")}
                            active={filters.hatirlatma === "hakem_bekliyor"}
                        />
                    </div>

                    <section className="rounded-2xl border border-white/10 bg-[#121A2F]/80 p-5 overflow-hidden">
                        <h3 className="text-sm font-semibold text-slate-200 mb-4 flex items-center gap-2">
                            <Scale className="h-4 w-4 text-violet-400" />
                            Süreç pipeline — aşama dağılımı
                        </h3>
                        <div className="space-y-3">
                            {TAHKIM_ASAMALARI.map((a) => {
                                const count = ozet.asama?.[a.value] || 0;
                                const pct = Math.round((count / asamaMax) * 100);
                                return (
                                    <button
                                        key={a.value}
                                        type="button"
                                        onClick={() => {
                                            setFilters((f) => ({
                                                ...f,
                                                tahkim_asamasi: a.value,
                                            }));
                                            setTab("liste");
                                        }}
                                        className="w-full group text-left"
                                    >
                                        <div className="flex justify-between text-xs mb-1">
                                            <span className="text-slate-400 group-hover:text-violet-200 transition">
                                                {a.label}
                                            </span>
                                            <span className="font-mono text-slate-200">{count}</span>
                                        </div>
                                        <div className="h-2 rounded-full bg-[#0B0F19] overflow-hidden">
                                            <div
                                                className={`h-full rounded-full transition-all ${ASAMA_COLORS[a.value] || "bg-violet-500"}`}
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                </div>
            )}

            {tab === "kanban" && (
                <TahkimKanban
                    columns={kanban?.columns}
                    loading={loading}
                    onRefresh={() => {
                        loadKanban();
                        loadOzet();
                    }}
                />
            )}

            {tab === "liste" && (
                <div className="space-y-4">
                    {selected.size > 0 && (
                        <div className="flex flex-wrap gap-2 items-center p-4 rounded-xl border border-violet-500/35 bg-violet-500/8">
                            <span className="text-sm text-violet-200 font-semibold">
                                {selected.size} dosya seçili
                            </span>
                            <select
                                value={topluAsama}
                                onChange={(e) => setTopluAsama(e.target.value)}
                                className="rounded-lg bg-[#0B0F19] border border-white/10 text-sm px-2 py-1.5 text-white"
                            >
                                <option value="">Aşama değiştir…</option>
                                {TAHKIM_ASAMALARI.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                            <input
                                placeholder="Toplu not ekle…"
                                value={topluNot}
                                onChange={(e) => setTopluNot(e.target.value)}
                                className="rounded-lg bg-[#0B0F19] border border-white/10 text-sm px-2 py-1.5 text-white flex-1 min-w-[140px]"
                            />
                            <button
                                type="button"
                                onClick={topluUygula}
                                className="px-4 py-1.5 rounded-lg bg-violet-600 text-white text-sm font-semibold"
                            >
                                Toplu uygula
                            </button>
                        </div>
                    )}
                    <div className="overflow-x-auto rounded-2xl border border-white/10 shadow-lg shadow-black/20">
                        <table className="w-full text-xs">
                            <thead className="bg-[#121A2F] text-slate-500 uppercase tracking-wider">
                                <tr>
                                    <th className="p-3 w-10" />
                                    <th className="p-3 text-left">Dosya</th>
                                    <th className="p-3 text-left">Müvekkil</th>
                                    <th className="p-3 text-left">Aşama</th>
                                    <th className="p-3 text-left">Ödeme</th>
                                    <th className="p-3 text-left">Hakem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {liste.map((row) => (
                                    <tr
                                        key={row.id}
                                        className="border-t border-white/5 hover:bg-violet-500/5 transition"
                                    >
                                        <td className="p-3">
                                            <input
                                                type="checkbox"
                                                checked={selected.has(row.id)}
                                                onChange={() => toggleSelect(row.id)}
                                                className="rounded"
                                            />
                                        </td>
                                        <td className="p-3">
                                            <Link
                                                to={`/e/tahkim-dosyalari/${row.id}`}
                                                className="text-[#F3E5AB] font-mono hover:underline"
                                            >
                                                {row.dosya_no || row.id}
                                            </Link>
                                        </td>
                                        <td className="p-3 text-slate-300">
                                            {row.muvekkil_ad_soyad}
                                        </td>
                                        <td className="p-3">
                                            <span className="px-2 py-0.5 rounded border border-violet-500/25 text-violet-200 bg-violet-500/10">
                                                {row.tahkim_asamasi_display || row.tahkim_asamasi}
                                            </span>
                                        </td>
                                        <td className="p-3">
                                            <span
                                                className={
                                                    row.son_odeme_gecikme
                                                        ? "text-red-300 font-medium"
                                                        : "text-slate-400"
                                                }
                                            >
                                                {row.odeme_durumu_display || row.odeme_durumu}
                                            </span>
                                        </td>
                                        <td className="p-3 text-slate-400">{row.hakem || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {tab === "on_tahkim" && <TahkimOnTahkimGelismis q={q} />}

            {tab === "basvuru_asamasi" && <TahkimBasvuruAsamasiPanel q={q} />}

            {tab === "rapor" && (
                <div className="space-y-6">
                    <TahkimRaporPdfPanel q={q} filters={filters} />
                    <section className="rounded-2xl border border-white/10 bg-[#121A2F]/80 p-5">
                        <h3 className="text-sm font-semibold text-slate-200 mb-4 flex items-center gap-2">
                            <Stethoscope className="h-4 w-4 text-[#D4AF37]" />
                            Sigorta şirketine göre dosya dağılımı (canlı)
                        </h3>
                        <ul className="space-y-1 max-h-[20rem] overflow-y-auto">
                            {sirketRapor.map((r, i) => (
                                <li
                                    key={i}
                                    className="flex justify-between items-center text-sm py-2 px-2 rounded-lg hover:bg-white/5 border-b border-white/5 last:border-0"
                                >
                                    <span className="text-slate-300">
                                        {r.sigorta_sirketi || "(belirtilmemiş)"}
                                    </span>
                                    <span className="font-mono text-violet-300 bg-violet-500/10 px-2 py-0.5 rounded">
                                        {r.adet}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </section>
                </div>
            )}

            {loading && tab !== "kanban" && tab !== "on_tahkim" && tab !== "basvuru_asamasi" && (
                <div className="flex justify-center py-10">
                    <Loader2 className="h-7 w-7 animate-spin text-violet-400" />
                </div>
            )}
        </div>
    );
}
